import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Drawer, DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtDate } from '@/shared/lib/format'
import type { CandidateDto } from '@/shared/api/types'
import { getCoalition, listCandidates } from './api'
import { CoalitionFormDialog } from './CoalitionFormDialog'
import {
  EntityChip, Pill, Swatch, coalitionStatusLabel, coalitionStatusTone, count,
  candidateStatusLabel, roleLabel, seatTypeLabel,
} from './vocab'

const CANDIDATE_LIMIT = 200

/**
 * One coalition: who is in it, and who reaches it.
 *
 * ## Two ways to be a coalition's candidate, and they are not the same fact
 *
 * A party candidate inherits their party's open membership — `coalitionDerived`
 * is true, and the alliance holds them only for as long as the party stays. An
 * independent may sit in the coalition DIRECTLY — `coalitionDerived` is false,
 * and no party stands between them and it. "UDA, therefore Kenya Kwanza" and
 * "independent, aligned to Kenya Kwanza" are different claims, so they are two
 * lists here rather than one chip repeated.
 *
 * ## There is no delete, and that is deliberate
 *
 * The server publishes no DELETE route for a coalition. A dissolved alliance's
 * membership record has to survive it — otherwise "who was in Azimio in 2022"
 * becomes unanswerable the moment somebody tidies up. The panel says so in words
 * instead of showing a disabled button whose reason lives in a tooltip nobody can
 * reach.
 */
export function CoalitionDrawer({ coalitionId, partyColours, onClose, onChanged }: {
  coalitionId: string | null
  /** Party id → `colourHex`. Memberships carry the coalition's colour, never the party's. */
  partyColours?: Record<string, string | null>
  onClose: () => void
  onChanged: () => void
}) {
  const canWrite = useCan('politics:coalition')
  const [editing, setEditing] = useState(false)

  const coalition = useLoad(
    () => (coalitionId ? getCoalition(coalitionId) : Promise.resolve(null)), [coalitionId])
  const candidates = useLoad(
    () => (coalitionId
      ? listCandidates({ coalitionId }, 0, CANDIDATE_LIMIT)
      : Promise.resolve(null)),
    [coalitionId])

  const row = coalition.data
  const members = row?.members ?? []
  const all = candidates.data?.content ?? []
  const derived = all.filter((c) => c.coalitionDerived)
  const direct = all.filter((c) => !c.coalitionDerived)
  const total = candidates.data?.totalElements ?? null

  return (
    <>
      <Drawer
        open={coalitionId !== null}
        onClose={onClose}
        width="lg"
        title={row ? row.name : 'Coalition'}
        subtitle={row ? `${row.code}${row.shortName ? ` · ${row.shortName}` : ''}` : undefined}
        right={row ? <Swatch colourHex={row.colourHex} className="h-4 w-4" /> : undefined}
        footer={(
          <>
            {!canWrite && (
              <span className="text-erp-xs text-muted-foreground mr-auto">
                Editing needs the <code>politics:coalition</code> authority.
              </span>
            )}
            <button type="button" className="erp-btn h-9 text-erp-sm" onClick={onClose}>Close</button>
            <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
              disabled={!row || !canWrite} onClick={() => setEditing(true)}>
              Edit
            </button>
          </>
        )}
      >
        <ErrorStrip message={coalition.error ?? candidates.error} />

        {coalition.loading && !row ? <LoadingBlock label="Loading the coalition…" /> : row && (
          <>
            <DrawerSection title="Identity">
              <DrawerField label="Code" value={row.code} mono />
              <DrawerField label="Name" value={row.name} />
              <DrawerField label="Short name" value={row.shortName ?? '—'} />
              <DrawerField label="Colour" value={(
                <span className="inline-flex items-center gap-2">
                  <Swatch colourHex={row.colourHex} />
                  <span className="font-mono text-erp-xs">
                    {row.colourHex ?? 'none declared — every chip for this coalition renders hollow'}
                  </span>
                </span>
              )} />
              <DrawerField label="Status" value={(
                <Pill tone={coalitionStatusTone(row.statusCode)}>
                  {coalitionStatusLabel(row.statusCode)}
                </Pill>
              )} />
              <DrawerField label="Formed" value={fmtDate(row.formedOn)} />
              <DrawerField label="Dissolved"
                value={row.dissolvedOn ? fmtDate(row.dissolvedOn) : 'still standing'} />
              <DrawerField label="Candidates" value={count(row.candidateCount)} />
              {row.notes && <DrawerField label="Notes" value={row.notes} />}
            </DrawerSection>

            <DrawerSection title={`Member parties (${members.length})`}>
              <div className="px-4 py-2 space-y-2">
                {members.length === 0 ? (
                  <p className="text-erp-sm text-muted-foreground py-2">
                    No party currently holds an open membership here. Parties join from their own
                    panel, on the Parties screen.
                  </p>
                ) : members.map((m) => (
                  <div key={m.id} className="flex flex-wrap items-center gap-2 text-erp-sm">
                    <EntityChip entity={{
                      code: m.partyCode,
                      name: m.partyName,
                      colourHex: partyColours?.[m.partyId] ?? null,
                    }} />
                    <span className="text-erp-xs text-muted-foreground">{roleLabel(m.roleCode)}</span>
                    <span className="ml-auto text-erp-xs tabular-nums text-muted-foreground">
                      since {fmtDate(m.joinedOn)}
                    </span>
                  </div>
                ))}
                {/* The detail read sends OPEN memberships only. A party that has
                    left is in that party's history, not here — saying so stops
                    this list being read as "everyone who was ever in it". */}
                <p className="text-erp-xs text-muted-foreground pt-1">
                  Open memberships only. A party that has left keeps the closed membership in its
                  own history.
                </p>
              </div>
            </DrawerSection>

            <DrawerSection title={`Candidates (${count(total)})`}>
              <div className="px-4 py-2 space-y-3">
                {candidates.loading && all.length === 0 ? (
                  <p className="text-erp-sm text-muted-foreground py-2">Loading candidates…</p>
                ) : all.length === 0 ? (
                  <p className="text-erp-sm text-muted-foreground py-2">
                    Nobody reaches this coalition — no member party has candidates and no
                    independent sits in it directly.
                  </p>
                ) : (
                  <>
                    <CandidateGroup
                      title="Through their party"
                      hint="They inherit this coalition from their party's open membership. If the party leaves, they leave with it."
                      rows={derived}
                      partyColours={partyColours}
                    />
                    <CandidateGroup
                      title="Directly in the coalition"
                      hint="Set on the candidate, not inherited. An independent aligned to the alliance with no party in between."
                      rows={direct}
                      partyColours={partyColours}
                    />
                    {total !== null && total > all.length && (
                      <Note tone="info">
                        Showing the first {all.length} of {total}. The{' '}
                        <Link className="erp-link" to={`/political-landscape/candidates?coalition=${row.id}`}>
                          Candidates screen
                        </Link>{' '}
                        pages through all of them.
                      </Note>
                    )}
                  </>
                )}
              </div>
            </DrawerSection>

            <DrawerSection title="Ending a coalition">
              <div className="px-4 py-3">
                <Note tone="info">
                  A coalition is never deleted — there is no route for it, deliberately. Its
                  membership record has to outlive the alliance, or nobody can answer who was in
                  it. Set the status to {coalitionStatusLabel('DISSOLVED')} with the date it ended;
                  every membership keeps pointing here.
                </Note>
              </div>
            </DrawerSection>
          </>
        )}
      </Drawer>

      <CoalitionFormDialog
        open={editing}
        coalition={row}
        onClose={() => setEditing(false)}
        onSaved={() => { setEditing(false); coalition.reload(); onChanged() }}
      />
    </>
  )
}

function CandidateGroup({ title, hint, rows, partyColours }: {
  title: string
  hint: string
  rows: CandidateDto[]
  partyColours?: Record<string, string | null>
}) {
  return (
    <div className="space-y-1.5">
      <div className="text-erp-xs font-semibold">{title}
        <span className="ml-1.5 font-normal text-muted-foreground tabular-nums">({rows.length})</span>
      </div>
      <p className="text-erp-xs text-muted-foreground">{hint}</p>
      {rows.length === 0 ? (
        <p className="text-erp-sm text-muted-foreground">None.</p>
      ) : rows.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center gap-2 text-erp-sm">
          <span className="font-medium truncate">{c.fullName}</span>
          <span className="text-erp-xs text-muted-foreground">
            {seatTypeLabel(c.seatTypeCode)} · {c.unitName ?? '—'}
          </span>
          {c.party
            ? <EntityChip entity={{
                code: c.party.code,
                name: c.party.name,
                shortName: c.party.shortName,
                colourHex: c.party.colourHex ?? partyColours?.[c.party.id] ?? null,
              }} />
            : <span className="text-erp-xs text-muted-foreground">Independent</span>}
          <span className="ml-auto text-erp-xs text-muted-foreground">
            {candidateStatusLabel(c.statusCode)}
          </span>
        </div>
      ))}
    </div>
  )
}
